import React, { useEffect, useState } from 'react';
import { Input, Button, Form, message, AutoComplete, Checkbox, Card, Spin } from 'antd';
import { SearchOutlined, FileSearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../baseUrl';
import AlertDialog from '../../../components/AlertDialog';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { generateAndDownloadHostelFeeBill } from './generateAndDownloadHostelFeeBill';
import { generateAndDownloadHDFBill } from './generateHDFBillPDF';

const PayHostelFee = () => {
    const [form] = Form.useForm();
    const [admissionNo, setAdmissionNo] = useState('');
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [mTechStatus, setMTechStatus] = useState(false);
    const [feeStatus, setFeeStatus] = useState([]);

    // Fetch admission number suggestions
    useEffect(() => {
        const searchAdmissionNumbers = async () => {
            if (admissionNo.length > 2) {
                try {
                    const response = await axios.get(`${baseUrl}/warden/search-applicant`, {
                        params: { query: admissionNo }
                    });
                    setOptions(response.data.map(no => ({ value: no })));
                } catch (error) {
                    console.error('Error searching admission numbers:', error);
                }
            } else {
                setOptions([]);
            }
        };

        searchAdmissionNumbers();
    }, [admissionNo]);

    // Fetch student profile
    const fetchProfile = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${baseUrl}/warden/get-hostel-applicant-info`, {
                params: { admissionNo: admissionNo }
            });
            setProfile(res.data);
            form.setFieldsValue(res.data);
        } catch (error) {
            console.log('error', error)
            message.error(error.response.data.msg);
        } finally {
            setLoading(false);
        }
    };

    // Fetch fee details
    const getFee = async () => {
        try {
            const res = await axios.get(`${baseUrl}/warden/get-individual-fee`, {
                params: { admissionNo: admissionNo, mTechStatus: mTechStatus }
            });
            setFeeStatus(res.data.map(fee => ({ ...fee, status: false })));
        } catch (error) {
            message.error('Failed to fetch fee details');
        }
    };

    // Handle fee payment status
    const handleFeePayment = (feeId, status) => {
        setFeeStatus(prevFees =>
            prevFees.map(fee =>
                fee.fee_id === feeId ? { ...fee, status: status } : fee
            )
        );
    };

    // Handle select all fees
    const handleSelectAll = (checked) => {
        setFeeStatus(prevFees =>
            prevFees.map(fee => ({ ...fee, status: checked }))
        );
    };

    const [disableStatus, setDisableStatus] = useState()
    const [totalFee, setTotalFee] = useState(0);
    const [open1, setOpen1] = useState("");
    const [open2, setOpen2] = useState("");
    const [modalText, setModalText] = useState("");
    const [modalHeading, setModalHeading] = useState("");

    useEffect(() => {
        setDisableStatus(feeStatus.every(fee => fee.status))
        calculateTotalFees()
    }, [feeStatus, mTechStatus])

    const calculateTotalFees = () => {
        let total = feeStatus.reduce((sum, fee) => {
            if (fee.status) {
                if (fee.fee_id == 11 || fee.fee_id == 12) {
                    if (['SC', 'ST', 'OEC'].includes(profile.admission_category) &&
                        (profile.programme === 'UG' || (profile.programme === 'PG' && !mTechStatus))) {
                        return sum + parseFloat(fee.value);
                    } else {
                        return sum + parseFloat(fee.value);
                    }
                }
                return sum + parseFloat(fee.value);
            }
            return sum;
        }, 0);

        setTotalFee(total);
    };

    const handlePay = async () => {
        try {
            const selectedFees = feeStatus.filter(fee => fee.status).map(fee => ({
                fee_id: fee.fee_id,
                fee_name: fee.fee_name,
                value: parseFloat(fee.value)
            }));

            const feeDetails = {
                fees: selectedFees,
                mTechStatus,
            };

            const response = await axios.post(`${baseUrl}/clerk-a1/store-hostel-fee`, {
                admissionNo,
                totalAmount: totalFee,
                feeDetails: feeDetails
            });

            if (response.data) {
                await generateAndDownloadHostelFeeBill(
                    feeStatus.filter(fee => fee.fee_id !== 13),
                    { ...profile, receipt_no: response.data.receipt_no }
                );

                await generateAndDownloadHDFBill(
                    feeStatus.filter(fee => fee.fee_id === 13),
                    { ...profile, receipt_no: response.data.receipt_no }
                );
                
                setModalHeading("Success!");
                setModalText("Receipt generated successfully");
                setOpen1(true);

                // Reset form
                setFeeStatus([]);
                setTotalFee(0);
                setProfile(null);
                setAdmissionNo('');
                form.resetFields();
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            setModalHeading("Error");
            setModalText("Failed to process payment. Please try again.");
            setOpen1(true);
        }
    };

    // Render student details
    const renderStudentDetails = () => {
        const detailsToShow = [
            { label: 'Student Name', value: profile?.name },
            { label: 'Admission Number', value: profile?.admission_no },
            { label: 'Department', value: profile?.department },
            { label: 'Programme', value: profile?.programme },
            { label: 'Year of Admission', value: profile?.year_of_admission },
            { label: 'Current Year', value: profile?.current_year },
            { label: 'Admission Category', value: profile?.admission_category },
            { label: 'Gender', value: profile?.gender },
        ];

        return (
            <div className="grid grid-cols-2 gap-4 mb-6">
                {detailsToShow.map((detail, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-md">
                        <div className="text-xs text-gray-500 mb-1">{detail.label}</div>
                        <div className="font-semibold text-gray-800">{detail.value || 'N/A'}</div>
                    </div>
                ))}
            </div>
        );
    };

    const [editMode, setEditMode] = useState({});
    
    const handleEditMode = (feeId) => {
        setEditMode(prev => ({ ...prev, [feeId]: !prev[feeId] }));
    };

    const handleValueChange = (feeId, newValue) => {
        setFeeStatus(feeStatus.map(fee =>
            fee.fee_id === feeId ? { ...fee, value: parseFloat(newValue) || 0 } : fee
        ));
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <Card
                title="Hostel Fee Payment"
                extra={
                    <div className="flex items-center space-x-4">
                        <AutoComplete
                            value={admissionNo}
                            options={options}
                            onSelect={setAdmissionNo}
                            onSearch={setAdmissionNo}
                            placeholder="Enter Admission No"
                            className="w-64"
                        />
                        <Button
                            type="seconday"
                            icon={<SearchOutlined />}
                            onClick={fetchProfile}
                            loading={loading}
                        >
                            View Profile
                        </Button>
                    </div>
                }
            >
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Spin size="large" />
                    </div>
                ) : profile ? (
                    <>
                        {renderStudentDetails()}

                        {profile.programme === 'PG' &&
                            ['SC', 'ST', 'OEC'].includes(profile.scholarship_category) && (
                                <div className="mb-6 flex items-center justify-end">
                                    <Checkbox
                                        checked={mTechStatus}
                                        onChange={(e) => setMTechStatus(e.target.checked)}
                                    >
                                        M-Tech Programme
                                    </Checkbox>
                                </div>
                            )}

                        <Button
                            type="default"
                            icon={<FileSearchOutlined />}
                            onClick={getFee}
                            className="mb-4"
                        >
                            Fetch Fee Details
                        </Button>

                        {feeStatus.length > 0 && (
                            <Card 
                                title="Fee Details" 
                                extra={
                                    <Checkbox
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        checked={feeStatus.length > 0 && feeStatus.every(fee => fee.status)}
                                        indeterminate={feeStatus.some(fee => fee.status) && !feeStatus.every(fee => fee.status)}
                                    >
                                        Select All
                                    </Checkbox>
                                }
                                className="mt-4"
                            >
                                {feeStatus.map((fee) => (
                                    <div
                                        key={fee.fee_id}
                                        className="flex justify-between items-center border-b py-3 last:border-b-0"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Checkbox
                                                checked={fee.status}
                                                onChange={(e) => handleFeePayment(fee.fee_id, e.target.checked)}
                                            />
                                            <span className="font-medium">{fee.fee_name}</span>
                                        </div>
                                        <div className="text-right">
                                            {fee.fee_id === 10 || fee.fee_id === 11 || fee.fee_id === 1? (
                                                <>
                                                    {editMode[fee.fee_id] ? (
                                                        <Input
                                                            type="number"
                                                            value={fee.value}
                                                            onChange={(e) => handleValueChange(fee.fee_id, e.target.value)}
                                                            className="w-24"
                                                        />
                                                    ) : (
                                                        <span>Rs. {fee.value}/-</span>
                                                    )}
                                                    <button
                                                        onClick={() => handleEditMode(fee.fee_id)}
                                                        className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                    >
                                                        {editMode[fee.fee_id] ? 'Save' : 'Edit'}
                                                    </button>
                                                </>
                                            ) : (
                                                <span className="font-semibold text-gray-700">
                                                    Rs. {fee.value}/-
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div className='flex flex-col py-2 items-end gap-2'>
                                    <div className="mt-4">
                                        <strong>Total Fee: Rs. {totalFee}/-</strong>
                                    </div>
                                    <button 
                                        disabled={!disableStatus} 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            setModalHeading("Confirm")
                                            setModalText("Do you wish to continue.")
                                            setOpen2(true)
                                        }} 
                                        className={disableStatus ? "bg-stone-800 text-white px-4 py-3 rounded-lg text-sm" : "bg-[#e0e0e0] text-[#a6a6a6] px-4 py-3 rounded-lg text-sm"}
                                    >
                                        Pay Fee
                                    </button>
                                </div>
                            </Card>
                        )}
                    </>
                ) : (
                    <div className="text-center text-gray-500 py-10">
                        Enter an Admission Number to view student details
                    </div>
                )}
            </Card>
            <AlertDialog
                open={open1}
                setOpen={setOpen1}
                modalHeading={modalHeading}
                modalText={modalText}
            />
            <ConfirmDialog
                open={open2}
                setOpen={setOpen2}
                modalHeading={modalHeading}
                modalText={modalText}
                confirmFunction={() => handlePay(profile)}
            />
        </div>
    );
};

export default PayHostelFee;